import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import { API_URL } from '../../config/api';

const PLATFORMS = [
  {
    id: 'DESKTOP_MAC',
    label: 'Desktop Mac',
    downloadHint: 'https://github.com/techellipticadigim/workloop-distribution/releases/download/v0.6.0/Workloop-0.6.0-mac-arm64.dmg',
  },
  {
    id: 'DESKTOP_WIN',
    label: 'Desktop Windows',
    downloadHint: 'https://github.com/techellipticadigim/workloop-distribution/releases/download/v0.6.0/Workloop-Portable-0.6.0-x64.exe',
  },
  { id: 'ANDROID', label: 'Android', downloadHint: 'https://play.google.com/store/apps/details?id=...' },
  { id: 'IOS', label: 'iOS', downloadHint: 'https://apps.apple.com/app/id...' },
];

const emptyForm = () => ({
  latestVersionCode: '',
  latestVersionName: '',
  minRequiredVersionCode: '',
  releaseNotes: '',
  downloadUrl: '',
});

const desktopPrefill = (item) => ({
  latestVersionCode: '600',
  latestVersionName: '0.6.0',
  minRequiredVersionCode: '600',
  releaseNotes: '',
  downloadUrl: item.downloadHint,
});

const AppUpdateConfigPanel = () => {
  const dispatch = useDispatch();
  const [platform, setPlatform] = useState('DESKTOP_WIN');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedByPlatform, setSavedByPlatform] = useState({});

  const [formData, setFormData] = useState(emptyForm());

  const activePlatform = PLATFORMS.find((item) => item.id === platform) || PLATFORMS[0];
  const isDesktopPlatform = platform === 'DESKTOP_MAC' || platform === 'DESKTOP_WIN';
  const configExists = Boolean(savedByPlatform[platform]);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const applyConfig = useCallback((platformId, config) => {
    const item = PLATFORMS.find((row) => row.id === platformId);
    if (config && (config.platform || config.latestVersionCode)) {
      setFormData({
        latestVersionCode: config.latestVersionCode ?? '',
        latestVersionName: config.latestVersionName || '',
        minRequiredVersionCode: config.minRequiredVersionCode ?? '',
        releaseNotes: config.releaseNotes || '',
        downloadUrl: config.downloadUrl || '',
      });
      return;
    }
    if (platformId === 'DESKTOP_MAC' || platformId === 'DESKTOP_WIN') {
      setFormData(desktopPrefill(item));
      return;
    }
    setFormData(emptyForm());
  }, []);

  const fetchAll = useCallback(async (selectedId = platform) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let configs = [];
      try {
        const listed = await axios.get(`${API_URL}/app-updates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        configs = Array.isArray(listed.data) ? listed.data : [];
      } catch {
        const rows = await Promise.all(PLATFORMS.map(async (item) => {
          try {
            const response = await axios.get(`${API_URL}/app-updates/${item.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
          } catch {
            return null;
          }
        }));
        configs = rows.filter(Boolean);
      }
      const next = {};
      configs.forEach((config) => {
        const id = String(config?.platform || '').toUpperCase();
        if (id) next[id] = config;
      });
      setSavedByPlatform(next);
      applyConfig(selectedId, next[selectedId]);
    } finally {
      setLoading(false);
    }
  }, [applyConfig, platform]);

  useEffect(() => {
    fetchAll(platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        platform,
        latestVersionCode: parseInt(formData.latestVersionCode, 10),
        latestVersionName: formData.latestVersionName,
        minRequiredVersionCode: parseInt(formData.minRequiredVersionCode, 10),
        releaseNotes: formData.releaseNotes,
        downloadUrl: formData.downloadUrl,
      };

      if (Number.isNaN(payload.latestVersionCode) || Number.isNaN(payload.minRequiredVersionCode)) {
        throw new Error('Version codes must be numbers');
      }

      const saved = await axios.put(`${API_URL}/app-updates/${platform}`, payload, {
        headers: authHeaders(),
      });
      const savedConfig = saved.data || { ...payload, platform };
      setSavedByPlatform((prev) => ({ ...prev, [platform]: savedConfig }));
      applyConfig(platform, savedConfig);

      dispatch(addNotification({
        type: 'success',
        title: 'Saved to database',
        message: `${activePlatform.label} is stored in Mongo (app_update_configs). Desktop apps read that row on launch.`,
      }));
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to save configuration';
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof msg === 'string' ? msg : 'Failed to save configuration',
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">App Updates</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Each platform needs its own Save. Desktop Windows/Mac are separate from Android.
            Saving here updates Electron gates and workloop.chat Download.
          </p>
        </div>

        <div className="flex flex-wrap bg-gray-100 dark:bg-gray-700 p-1 rounded-lg gap-0.5">
          {PLATFORMS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setPlatform(item.id)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                platform === item.id
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {item.label}
              {savedByPlatform[item.id] ? (
                <span className="ml-1 text-[10px] font-semibold text-emerald-600">saved</span>
              ) : (
                <span className="ml-1 text-[10px] font-semibold text-amber-600">empty</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {!configExists && !loading && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
            <strong>No {activePlatform.label} row in Mongo yet.</strong>{' '}
            Click Save to write this platform into Mongo (<code className="text-xs">app_update_configs</code>).
            Desktop 0.6.0 uses versionCode <strong>600</strong>. Set Minimum to <strong>600</strong> to force older installs to update.
          </div>
        )}

        {configExists && (
          <div className="mb-6 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-900 dark:text-emerald-100">
            <div className="font-medium mb-1">Loaded from database</div>
            <div>
              {activePlatform.label}: min {savedByPlatform[platform].minRequiredVersionCode}
              {' · '}latest {savedByPlatform[platform].latestVersionCode}
              {savedByPlatform[platform].latestVersionName
                ? ` (${savedByPlatform[platform].latestVersionName})`
                : ''}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Latest Version Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="latestVersionCode"
                  value={formData.latestVersionCode}
                  onChange={handleInputChange}
                  required
                  min="1"
                  placeholder="e.g. 600"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Integer used for comparisons.
                  {isDesktopPlatform
                    ? <> Desktop <strong>0.6.0</strong> = code <strong>600</strong>.</>
                    : <> Android uses versionCode (e.g. 17).</>}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Latest Version Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="latestVersionName"
                  value={formData.latestVersionName}
                  onChange={handleInputChange}
                  required
                  placeholder='e.g. "0.6.0"'
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Minimum Required Version Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="minRequiredVersionCode"
                  value={formData.minRequiredVersionCode}
                  onChange={handleInputChange}
                  required
                  min="0"
                  placeholder="e.g. 600"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  If the app&apos;s version code is lower than this, the update is <strong>MANDATORY</strong>.
                  If it is equal or higher, but lower than Latest, the update is <strong>OPTIONAL</strong>.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Download URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="downloadUrl"
                  value={formData.downloadUrl}
                  onChange={handleInputChange}
                  required
                  placeholder={activePlatform.downloadHint}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Release Notes
                </label>
                <textarea
                  name="releaseNotes"
                  value={formData.releaseNotes}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="What's new in this version..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : `Save ${activePlatform.label} Config`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AppUpdateConfigPanel;
